import { RotuloDeFiltro } from "@/components/RotuloDeFiltro";
import { Slider } from "@/components/ui/slider";
import { useValorSostenido } from "@/hooks/useValorSostenido";
import { cn } from "@/lib/utils";

interface CampoDeRangoProps {
  rotulo: string;
  /** Una manija o dos: lo decide el largo del arreglo, no una prop aparte. */
  valor: number[];
  onCambio: (valor: number[]) => void;
  min: number;
  max: number;
  paso?: number;
  /** Cómo se lee el valor arriba a la derecha ("1 – 200 ha", "20 km"). */
  leerValor: (valor: number[]) => string;
  /** Los dos extremos de la escala, debajo de la pista. */
  extremoMin: string;
  extremoMax: string;
  deshabilitado?: boolean;
  /**
   * Por qué está deshabilitado. Un control apagado sin explicación deja al
   * usuario mirando algo que no responde: acá siempre hay un motivo concreto
   * —hoy, que el filtro de distancia exige comuna— y se dice.
   */
  motivo?: string;
  className?: string;
}

/**
 * Un slider con su rótulo, su lectura y sus extremos.
 *
 * La LECTURA va arriba a la derecha y no bajo la manija: bajo la manija se
 * mueve con ella, y en un rango de dos extremos las dos etiquetas se pisan
 * cuando los valores se acercan.
 *
 * Los extremos de la escala van debajo y siempre visibles porque son lo que
 * vuelve interpretable la posición: sin ellos, una manija a media pista no
 * dice si son 100 hectáreas o 100 mil.
 */
export function CampoDeRango({
  rotulo,
  valor,
  onCambio,
  min,
  max,
  paso = 1,
  leerValor,
  extremoMin,
  extremoMax,
  deshabilitado = false,
  motivo,
  className,
}: CampoDeRangoProps) {
  /**
   * LO QUE LA PERSONA ELIGIÓ Y LA PROP TODAVÍA NO REFLEJA.
   *
   * El arrastre NO se avisa: `onCambio` acaba en la url, y cada url nueva
   * dispara las dos consultas del paso 2 —el catálogo y los puntos del mapa—
   * más su preflight CORS. Radix llama a `onValueChange` en CADA movimiento del
   * puntero, así que cablearlo derecho mandaba decenas de peticiones al
   * arrastrar la manija de un extremo al otro: se apilaban en el servidor y una
   * respuesta llegó a tardar 15 s. `onValueCommit` es "la persona terminó de
   * elegir" y es el único que avisa.
   *
   * Lo elegido se SOSTIENE hasta que la prop llega, y por qué hace falta está
   * contado entero en `useValorSostenido` — el mismo hook lo usa el slider de
   * los ejes del gráfico.
   */
  const { mostrado, elegir } = useValorSostenido(valor);

  /**
   * ¿El control está acotando algo, o abarca su escala entera?
   *
   * Con seis filtros seguidos, todos con su cifra en negrita, no se ve cuáles
   * están puestos: hay que leer cada número y compararlo contra sus extremos.
   * En tinta apagada, un filtro abierto de par en par se lee como lo que es
   * —no está filtrando— sin tener que compararlo con nada.
   *
   * Se DEDUCE del valor y no se pide por prop: preguntárselo al llamador sería
   * un segundo lugar donde decidir lo mismo, y el día que discreparan la
   * pantalla diría que un filtro está abierto mientras el servidor recibe uno
   * puesto.
   */
  const acota =
    mostrado.length > 1
      ? mostrado[0] !== min || mostrado[mostrado.length - 1] !== max
      : // Con una sola manija el extremo abierto es el de ARRIBA: es un TOPE
        // ("hasta 30 km"), no una banda, así que en el máximo no acota nada.
        mostrado[0] !== max;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <RotuloDeFiltro className={cn(deshabilitado && "opacity-50")}>
          {rotulo}
        </RotuloDeFiltro>
        {/* LA LECTURA ES EL DATO, no metadata: el diseño la pone en 800 y en
            tinta plena. Iba sin peso —o sea en 400— y en gris de metadata, así
            que el número que dice qué se está filtrando era lo más liviano del
            control. */}
        <span
          className={cn(
            "font-mono text-xs font-extrabold tabular-nums",
            acota && !deshabilitado ? "text-ink" : "text-ink-hint",
          )}
        >
          {deshabilitado ? "—" : leerValor(mostrado)}
        </span>
      </div>

      <Slider
        aria-label={rotulo}
        value={mostrado}
        // Mueve la manija y la lectura, y NADA MÁS: no sale de este componente.
        onValueChange={elegir}
        // El gesto terminó: se avisa, y lo elegido SE QUEDA en pantalla hasta
        // que la prop lo alcance. Soltarlo acá era el salto al valor anterior.
        onValueCommit={(commiteado) => {
          elegir(commiteado);
          onCambio(commiteado);
        }}
        min={min}
        max={max}
        step={paso}
        disabled={deshabilitado}
      />

      <div className="text-ink-ghost flex justify-between text-xs">
        <span>{extremoMin}</span>
        <span>{extremoMax}</span>
      </div>

      {deshabilitado && motivo && <p className="text-ink-hint text-xs">{motivo}</p>}
    </div>
  );
}
